import { useCallback } from 'react';
import { atom, getDefaultStore } from 'jotai';
import uuid from 'react-native-uuid';
import { useOpenDeeplink } from '../useOpenDeeplink';
import { useInstanceId } from '../useInstanceId';

export type SheetResult<T = unknown> =
  | { type: 'confirm'; data: T }
  | { type: 'cancel' };

export type SheetRegistration<P = unknown, R = unknown> = {
  sheetId: string;
  params: P;
  resolve: (result: SheetResult<R>) => void;
  // BottomSheetHost modal이 부모 surface를 가릴 때 부모의 viewDidDisappear로
  // foreground가 false로 떨어지지 않도록 ForegroundProvider가 참조.
  parentInstanceId: string;
};

export const sheetRegistrationAtom = atom<Record<string, SheetRegistration>>(
  {},
);

const removeRegistration = (requestId: string) => {
  const store = getDefaultStore();
  store.set(sheetRegistrationAtom, prev => {
    if (!(requestId in prev)) return prev;
    const next = { ...prev };
    delete next[requestId];
    return next;
  });
};

export const useBottomSheetRoute = () => {
  const { openDeeplink } = useOpenDeeplink();
  const parentInstanceId = useInstanceId();

  const open = useCallback(
    <P = unknown, R = unknown>(
      sheetId: string,
      params: P,
    ): Promise<SheetResult<R>> => {
      const requestId = String(uuid.v4());
      const store = getDefaultStore();

      return new Promise<SheetResult<R>>(resolve => {
        store.set(sheetRegistrationAtom, prev => ({
          ...prev,
          [requestId]: {
            sheetId,
            params: params as unknown,
            resolve: (result: SheetResult<unknown>) => {
              removeRegistration(requestId);
              resolve(result as SheetResult<R>);
            },
            parentInstanceId,
          },
        }));

        openDeeplink(
          `ably://react-modal/App?screen=BottomSheetHost&requestId=${requestId}`,
        );
      });
    },
    [openDeeplink, parentInstanceId],
  );

  return { open };
};
